import { ENEMIES } from '../../data/enemies';
import { SIM_HZ } from '../../data/tuning';
import {
  AMBER_TICKS,
  angleTo,
  BURST_TICKS,
  EN02,
  inCone,
  SWEEP_TICKS,
  sweepAngle,
  sweepsCompleted,
  type Projectile,
  type Searchlight,
} from './law-helicopter';

/**
 * EN-44 Reflector del muro (GDD §8.3, §8.2, issue #130). An EN-02 without the helicopter: fixed on the border
 * wall, several searchlights sweep in sync so the pilot reads a light pattern instead of one cone. It reuses the
 * cone, the amber and the burst of EN-02 (#84) and, like the whole law, is never destroyed (GDD §3.4). Pure and
 * deterministic: the sweep phase is derived from the global tick of the level and the `syncGroup`, so every
 * reflector of a group has the same angle on the same tick and the result is the same after a respawn.
 */

export const EN44 = ENEMIES['EN-44'];

export interface WallSearchlightState {
  /** Ticks since the level started, for the exposure counter (the sweep uses the global tick). */
  ticks: number;
  angle: number;
  searchlight: Searchlight;
  /** Consecutive ticks the pilot has been inside the cone. */
  exposure: number;
  bursts: number;
  projectiles: Projectile[];
  /** The sweep offset in ticks: `syncGroup` and `phaseOffset` of the level data. */
  phaseOffsetTicks: number;
}

/** Phase offset in ticks for a group: half a sweep per extra group by default (proposal, #130). */
export const phaseOffsetFor = (syncGroup: number, phaseOffset?: number): number =>
  Math.round((phaseOffset ?? 0.5 * (Math.max(1, syncGroup) - 1)) * SWEEP_TICKS);

export function createWallSearchlight(params: { syncGroup?: number; phaseOffset?: number } = {}): WallSearchlightState {
  const phaseOffsetTicks = phaseOffsetFor(params.syncGroup ?? 1, params.phaseOffset);
  return { ticks: 0, angle: sweepAngle(phaseOffsetTicks), searchlight: 'idle', exposure: 0, bursts: 0, projectiles: [], phaseOffsetTicks };
}

/** The sweep angle at a global tick: the shared triangle wave, offset by the group. */
export const angleAt = (state: WallSearchlightState, globalTick: number): number => sweepAngle(globalTick + state.phaseOffsetTicks);

export interface WallSearchlightStep {
  amber: boolean;
  burst: boolean;
}

/**
 * One tick of an active searchlight fixed at (x, y) with the pilot at `pilot`. The sweep is a function of the
 * global tick; the exposure counter is independent per reflector (proposal). A bribed pilot gets no exposure.
 */
export function stepWallSearchlight(
  state: WallSearchlightState,
  at: { x: number; y: number },
  pilot: { x: number; y: number },
  globalTick: number,
  bribed: boolean,
): WallSearchlightStep {
  const result: WallSearchlightStep = { amber: false, burst: false };
  state.ticks += 1;
  state.angle = angleAt(state, globalTick);

  if (!bribed && inCone(at, state.angle, pilot)) state.exposure += 1;
  else state.exposure = 0;

  const before = state.searchlight;
  state.searchlight = state.exposure >= AMBER_TICKS ? 'amber' : 'idle';
  if (before === 'idle' && state.searchlight === 'amber') result.amber = true;

  if (state.exposure >= BURST_TICKS) {
    const length = Math.hypot(pilot.x - at.x, pilot.y - at.y) || 1;
    const speed = EN02.projectileSpeed / SIM_HZ;
    for (let i = 0; i < EN02.projectilesPerBurst; i++) {
      state.projectiles.push({
        x: at.x,
        y: at.y,
        vx: ((pilot.x - at.x) / length) * speed,
        vy: ((pilot.y - at.y) / length) * speed,
        delay: i * EN02.projectileSpacingTicks,
      });
    }
    state.bursts += 1;
    state.exposure = 0;
    state.searchlight = 'idle';
    result.burst = true;
  }
  return result;
}

/** Moves the projectiles one tick and drops those outside the box (shared with EN-02). */
export function stepWallProjectiles(
  state: WallSearchlightState,
  box: { left: number; right: number; bottom: number; top: number },
): void {
  for (const projectile of state.projectiles) {
    if (projectile.delay > 0) {
      projectile.delay -= 1;
      continue;
    }
    projectile.x += projectile.vx;
    projectile.y += projectile.vy;
  }
  state.projectiles = state.projectiles.filter(
    (p) => p.delay > 0 || (p.x >= box.left && p.x <= box.right && p.y >= box.bottom && p.y <= box.top),
  );
}

// Keeps the shared helpers referenced for the readers of this module.
export { angleTo, inCone, sweepAngle, sweepsCompleted };
